import {Injectable} from '@angular/core';
import {map, Observable, switchMap, take, tap, timeout} from 'rxjs';
import {ProposalTile} from '../../model/proposal-tile';
import {environment} from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import {Proposal} from '../../model/proposal/proposal';
import {TranslateService} from '@ngx-translate/core';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {Store} from '@ngrx/store';
import {selectUserAndCompany} from '../../store/user/selectors/user.selectors';
import {ProposalFilterQuery} from '../../model/proposal-filter-query';
import {Offer} from '../../model/offer/offer';
import {ProposalStatus} from '../../enum/proposal-status';
import {OfferStatus} from '../../enum/offer-status';
import {ProposalNumber} from '../../model/proposal-number';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ProposalVersionHistoryEntry} from '../../model/proposal/proposal-version-history-entry';

@Injectable({
  providedIn: 'root'
})
export class ProposalService {

  constructor(private readonly http: HttpClient,
              private readonly translate: TranslateService,
              private readonly userStore: Store<UserAppState>,
              private readonly snackbar: MatSnackBar,
  ) {
  }

  filteredProposalTiles(filter: ProposalFilterQuery): Observable<ProposalTile[]> {
    let params = new HttpParams();
    Object.keys(filter).filter(key => filter[key] != null && filter[key] !== false).forEach(key =>
      params = params.append(key, filter[key])
    );
    return this.http.get<ProposalTile[]>(environment.backend + '/v1/proposals/tiles/', {params: params})
      .pipe(map(proposalTiles => this.mapProposalTiles(proposalTiles)));
  }

  private mapProposalTiles(proposalTiles: ProposalTile[]): ProposalTile[] {
    const mappedProposalTiles: ProposalTile[] = [];
    proposalTiles.forEach(proposalTile => {
      mappedProposalTiles.push(this.mapProposalTile(proposalTile));
    });
    return mappedProposalTiles;
  }

  saveProposal(proposal: Proposal) {
    return this.userStore.select(selectUserAndCompany).pipe(take(1),
      switchMap(result => {
        let modifiedProposal = new Proposal(proposal);
        modifiedProposal.partner.companyId = result.company?.companyId;
        modifiedProposal.creator.id = result.user?.id;
        return this.http.post(environment.backend + '/v1/proposals', modifiedProposal).pipe(
          // Set a timeout of 1 minute, if it lasts longer, then error will be thrown
          timeout(60000)
        );
      }),
      tap({
        next: () => {
          this.snackbar.open(this.translate.instant('createProposal.saved'), '', {
            panelClass: ['success-snackbar', 'custom-snackbar']
          });
        },
        error: () => {
          this.snackbar.open(this.translate.instant('createProposal.failed'), '', {
            panelClass: ['fail-snackbar', 'custom-snackbar'],
          });
        }
      }
      ));
  }

  editProposal(proposal: Proposal) {
    return this.userStore.select(selectUserAndCompany).pipe(take(1),
      switchMap(result => {
        let modifiedProposal = new Proposal(proposal);
        modifiedProposal.partner.companyId = result.company?.companyId;
        modifiedProposal.creator.id = result.user?.id;
        modifiedProposal.lastTimeEdited = new Date();

        const url = environment.backend + '/v1/proposals/' + proposal.proposalInformation.id;
        return this.http.patch(url, modifiedProposal).pipe(
          timeout(60000)
        );
      }),
      tap({
        next: () => {
          this.snackbar.open(this.translate.instant('editProposal.saved'), '', {
            panelClass: ['success-snackbar', 'custom-snackbar']
          });
        },
        error: () => {
          this.snackbar.open(this.translate.instant('editProposal.failed'), '', {
            panelClass: ['fail-snackbar', 'custom-snackbar'],
          });
        }
      })
    );
  }

  downloadEMLFile(proposalId: string, emailRecipient: string, pdfFile: Blob): Observable<Blob> {
    const formData = new FormData();
    formData.append("pdfFile", pdfFile, "proposal.pdf");

    return this.http.post(environment.backend + '/v1/proposals/download-eml/' + proposalId + '/' + emailRecipient, formData, { responseType: 'blob' });
  }

  getProposalTile(proposalId: string): Observable<ProposalTile> {
    return this.http.get<ProposalTile>(environment.backend + '/v1/proposals/tiles/' + proposalId)
      .pipe(map(proposalTile => this.mapProposalTile(proposalTile)
      ));
  }

  private mapProposalTile(proposalTile: ProposalTile) {
    return new ProposalTile(proposalTile);
  }

  getProposal(proposalId: string): Observable<Proposal> {

    return this.http.get<Proposal>(environment.backend + '/v1/proposals/' + proposalId)
      .pipe(map(proposal => new Proposal(proposal)));
  }

  getProposalHistory(proposalId: string): Observable<ProposalVersionHistoryEntry[]> {
    return this.http.get<any[]>(environment.backend + '/v1/proposals/' + proposalId + '/history')
      .pipe(map(arr => (arr ?? []).map(entry => new ProposalVersionHistoryEntry(entry))));
  }

  rejectInsuranceContract(rejectionId, offer: Offer) {
    if (rejectionId == null) {
      return this.http.post(environment.backend + '/v1/offers', offer);
    } else {
      return this.http.patch(environment.backend + '/v1/offers/' + rejectionId, {
        proposalId: offer.proposalId,
        rejectionText: offer.rejectionText,
        status: OfferStatus.REJECTION
      });
    }

  }

  retractRejections(rejectionId, proposalId) {
    return this.http.patch(environment.backend + '/v1/offers/' + rejectionId, {proposalId: proposalId,  status: OfferStatus.NOLONGERACTIVE});
  }

  retract(id: string) {
    return this.http.patch<Proposal>(environment.backend + '/v1/proposals/status/' + id, {status: ProposalStatus.RETRACTED})
      .pipe(tap({
          next: () => this.snackbar.open(this.translate.instant('proposalRetracted'), '', {
            panelClass: ['success-snackbar', 'custom-snackbar'],
          })
        }),
        map(proposal => new Proposal(proposal)));
  }

  addEmailToProposal(id: string, emailWithDate: string) {
    return this.http.patch<Proposal>(environment.backend + '/v1/proposals/email/' + id, {emailWithDate:emailWithDate})
      .pipe(tap({
          next: () => this.snackbar.open(this.translate.instant('proposalPDFSent'), '', {
            panelClass: ['success-snackbar', 'custom-snackbar'],
          })
        }),
        map(proposal => new Proposal(proposal)));
  }

  getProposalNumber(filter: ProposalFilterQuery): Observable<ProposalNumber> {
    let params = new HttpParams();
    Object.keys(filter).filter(key => filter[key] != null && filter[key] !== false).forEach(key =>
      params = params.append(key, filter[key])
    );
    return this.http.get<ProposalNumber>(environment.backend + '/v1/proposals/number', {params: params})
      .pipe(map(proposalNumber => this.mapProposalNumber(proposalNumber)
      ));
  }

  private mapProposalNumber(proposalNumber: ProposalNumber) {
    return new ProposalNumber(proposalNumber);
  }

  getInsuranceTax(): Observable<number> {
    return this.http.get<number>(environment.backend + '/v1/proposals/insurance_tax');
  }

  checkPartnerInfo(proposal$: Observable<Proposal>) {
    return proposal$.pipe(map((proposal) => {
        if (proposal) {
          const partner = proposal.partner;
          if ((partner.name === '' || partner.name === null) &&
              (partner.street === '' || partner.name === null) &&
              (partner.houseNumber === '' || partner.name === null) &&
              partner.addressSupplement === null &&
              (partner.postalCode === '' || partner.name === null) &&
              (partner.location === '' || partner.name === null) &&
              (partner.countryCode === ''|| partner.name === null)) {
            return true;
          }
        }

        return false;
      }
    ));
  }
}
